import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatMoney, API_BASE, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FileDown, EyeOff, Pencil, X, Save, Wallet, Trash2, Receipt } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function RateEditModal({ row, onClose, onSaved }) {
  const isSale = row.kind === "sale";
  const isPurchase = row.kind === "purchase";
  const perKg = isSale || isPurchase;
  const sizeKg = (s) => {
    const n = parseFloat(String(s || "").toLowerCase().replace("kg", "").trim());
    return isNaN(n) ? 0 : n;
  };  const computeAmt = (it) => {
    const fq = Number(it.filled_qty || 0);
    const r = Number(it.rate || 0);
    return perKg ? +(fq * sizeKg(it.size) * r).toFixed(2) : +(fq * r).toFixed(2);
  };
  const [items, setItems] = useState(
    (row.items || []).map((it) => ({ ...it, _origRate: it.rate }))
  );
  const [busy, setBusy] = useState(false);

  const setRate = (i, val) => {
    const next = [...items];
    const r = Number(val) || 0;
    next[i] = { ...next[i], rate: r };
    next[i].amount = computeAmt(next[i]);
    setItems(next);
  };
  const newTotal = items.reduce((s, it) => s + (it.amount || 0), 0);
  const oldTotal = row.debit + row.credit;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/transactions/${row.txn_id}/rates`, {
        items: items.map((it) => ({ brand: it.brand, size: it.size, rate: Number(it.rate) || 0 })),
      });
      toast.success("Rates updated · ledger refreshed");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="rate-edit-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="small-label text-gray-500">Edit rates · {row.kind}</div>
            <div className="font-heading font-black text-xl truncate">{row.description}</div>
            <div className="text-xs text-gray-500 mt-1">Date: {row.date} {row.challan_no ? `· Challan ${row.challan_no}` : ""}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-3">
          {items.length === 0 && <div className="text-sm text-gray-500">No items found.</div>}
          {items.map((it, i) => (
            <div key={i} className="card-flat p-3 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid={`rate-item-${i}`}>
              <div className="sm:col-span-2">
                <div className="small-label text-gray-500">Item</div>
                <div className="font-semibold">{it.brand} · {it.size}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Filled: <span className="data-num font-bold">{it.filled_qty}</span>{it.mt_qty ? ` · MT: ${it.mt_qty}` : ""}
                </div>
              </div>
              <div>
                <label className="small-label block mb-1">Rate (₹{perKg ? "/kg" : ""})</label>
                <input
                  type="number" step="0.01"
                  className="input-lg h-10 text-right data-num"
                  value={it.rate}
                  onChange={(e) => setRate(i, e.target.value)}
                  data-testid={`rate-input-${i}`}
                />
                {it.rate !== it._origRate && (
                  <div className="text-xs text-gray-500 mt-1">was ₹ {formatMoney(it._origRate)}{perKg ? "/kg" : ""}</div>
                )}
                {perKg && (
                  <div className="text-xs text-gray-500 mt-1">
                    {it.filled_qty} × {sizeKg(it.size)} kg × ₹{it.rate || 0}
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <div className="small-label text-gray-500">Line Amount</div>
                <div className="data-num text-xl font-bold">₹ {formatMoney(it.amount || 0)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-5 border-t border-gray-200 sticky bottom-0 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="small-label text-gray-500">New Total</div>
            <div className="data-num font-black text-2xl">₹ {formatMoney(newTotal)}</div>
          </div>
          {Math.abs(newTotal - oldTotal) > 0.01 && (
            <div className="text-xs text-[#B45309] mb-3">
              Was ₹ {formatMoney(oldTotal)} · {newTotal > oldTotal ? "+" : ""}{formatMoney(newTotal - oldTotal)} change
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm" data-testid="rate-cancel">Cancel</button>
            <button onClick={save} disabled={busy || items.length === 0} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="rate-save">
              <Save size={16} /> {busy ? "Saving…" : "Save & Recalculate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentEditModal({ row, onClose, onSaved }) {
  const isReceived = row.kind === "payment_received";
  const initialAmount = isReceived ? row.credit : row.debit;
  const [form, setForm] = useState({
    date: row.date || "",
    amount: initialAmount || 0,
    mode: row.mode || "cash",
    reference: row.reference || "",
    notes: row.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      await api.patch(`/payments/${row.payment_id}`, {
        date: form.date,
        amount: Number(form.amount),
        mode: form.mode,
        reference: form.reference,
        notes: form.notes,
      });
      toast.success("Payment updated · ledger refreshed");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="payment-edit-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="small-label text-gray-500">Edit · payment {isReceived ? "received" : "paid"}</div>
            <div className="font-heading font-black text-xl">₹ {formatMoney(initialAmount)}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <label className="small-label block mb-1">Date</label>
            <input type="date" className="input-lg" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="payment-edit-date" />
          </div>
          <div>
            <label className="small-label block mb-1">Amount ₹</label>
            <input type="number" step="0.01" className="input-lg text-right data-num" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="payment-edit-amount" />
          </div>
          <div>
            <label className="small-label block mb-1">Mode</label>
            <select className="input-lg" value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}
              data-testid="payment-edit-mode">
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank">Bank</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="small-label block mb-1">Reference</label>
            <input className="input-lg" value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="UTR / cheque no."
              data-testid="payment-edit-reference" />
          </div>
          <div className="col-span-2">
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="payment-edit-save">
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function BillNoEditModal({ row, onClose, onSaved }) {
  const [billNo, setBillNo] = useState(row.bill_no || "");
  const [billAmount, setBillAmount] = useState(row.bill_amount || "");
  const [busy, setBusy] = useState(false);
  const saleAmount = row.amount ?? ((row.debit || 0) + (row.credit || 0));
  const ba = Number(billAmount) || 0;
  const mismatch = ba > 0 && Math.abs(ba - saleAmount) > 0.5;
  const diff = ba - saleAmount;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/transactions/${row.txn_id}/bill-no`, {
        bill_no: billNo,
        bill_amount: billAmount === "" ? 0 : Number(billAmount) || 0,
      });
      toast.success("Bill details saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="bill-no-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="small-label text-gray-500">Bill details for</div>
            <div className="font-heading font-black text-lg">Challan {row.challan_no || <span className="text-gray-400 italic">no #</span>}</div>
            <div className="text-xs text-gray-500 mt-0.5">{row.date} · Sale ₹ {formatMoney(saleAmount)}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-1.5">Bill / Invoice number</label>
            <input
              className="input-lg"
              placeholder="e.g. INV-2026-0087"
              value={billNo}
              onChange={(e) => setBillNo(e.target.value)}
              autoFocus
              data-testid="bill-no-input"
            />
          </div>
          <div>
            <label className="small-label block mb-1.5">Bill amount (₹)</label>
            <input
              type="number" step="0.01"
              className={`input-lg text-right data-num ${mismatch ? "border-[#B91C1C] bg-[#FEE2E2]" : ""}`}
              placeholder={`Leave 0 to skip · sale is ${formatMoney(saleAmount)}`}
              value={billAmount}
              onChange={(e) => setBillAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              data-testid="bill-amount-input"
            />
            {mismatch ? (
              <div className="text-xs mt-1.5 flex items-center gap-1.5 text-[#B91C1C] font-semibold">
                <span className="inline-block w-2 h-2 bg-[#B91C1C] rounded-full" />
                Mismatch of ₹ {formatMoney(Math.abs(diff))} vs sale amount
                <span className="text-gray-500 font-normal">({diff > 0 ? "bill higher" : "bill lower"})</span>
              </div>
            ) : ba > 0 ? (
              <div className="text-xs mt-1.5 text-[#15803D]">Bill matches sale amount</div>
            ) : (
              <div className="text-xs mt-1.5 text-gray-500">Leave blank / 0 if bill amount is same as sale.</div>
            )}
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="bill-no-save">
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function PartyLedger() {
  const { user } = useAuth();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingBill, setEditingBill] = useState(null);
  const [editingOpening, setEditingOpening] = useState(false);
  const [openingDraft, setOpeningDraft] = useState(0);
  const [savingOpening, setSavingOpening] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ledger/${id}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const deletePayment = async (row) => {
    if (!window.confirm(`Delete this payment of ₹ ${formatMoney(row.debit || row.credit)} on ${row.date}?`)) return;
    try {
      await api.delete(`/payments/${row.payment_id}`);
      toast.success("Payment deleted · ledger refreshed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTxn = async (row) => {
    const total = (row.debit || 0) + (row.credit || 0);
    if (!window.confirm(`Delete this ${row.kind} entry of ₹ ${formatMoney(total)} on ${row.date}${row.challan_no ? ` (CH ${row.challan_no})` : ""}?\nStock and ledgers will be recomputed.`)) return;
    try {
      await api.delete(`/transactions/${row.txn_id}`);
      toast.success(`${row.kind} entry deleted · ledger refreshed`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveOpening = async () => {
    setSavingOpening(true);
    try {
      const p = data.party;
      await api.patch(`/parties/${id}`, {
        type: p.type, name: p.name, phone: p.phone || "",
        address: p.address || "", notes: p.notes || "",
        opening_balance: Number(openingDraft) || 0,
      });
      toast.success("Opening balance updated");
      setEditingOpening(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingOpening(false); }
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("lpg_token") || "";
    const res = await fetch(`${API_BASE}/ledger/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ledger_${data?.party?.name || id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (!data) return <div>Not found</div>;

  const p = data.party;
  const hideMoney = user?.role === "manager" && p.type === "vendor";

  return (
    <div className="space-y-6" data-testid="party-ledger-page">
      <div className="card-flat p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="small-label text-gray-500">{p.type}</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight truncate">{p.name}</h2>
          <div className="text-sm text-gray-600 break-words">{p.phone || "—"} · {p.address || "—"}</div>

          {!hideMoney && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-gray-50 border border-gray-200">
              <Wallet size={14} className="text-gray-500" />
              <span className="small-label text-gray-500">Opening</span>
              {!editingOpening ? (
                <>
                  <span className="data-num font-bold text-sm" data-testid="opening-balance-display">
                    ₹ {formatMoney(p.opening_balance || 0)}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => { setOpeningDraft(p.opening_balance || 0); setEditingOpening(true); }}
                      className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 ml-1"
                      data-testid="edit-opening-btn"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="number" step="0.01"
                    className="h-8 px-2 border border-gray-300 rounded-sm w-32 text-right data-num text-sm"
                    value={openingDraft}
                    onChange={(e) => setOpeningDraft(e.target.value)}
                    autoFocus
                    data-testid="opening-balance-input"
                  />
                  <button
                    onClick={saveOpening}
                    disabled={savingOpening}
                    className="h-8 px-3 bg-black text-white rounded-sm text-xs font-bold inline-flex items-center gap-1"
                    data-testid="save-opening-btn"
                  >
                    <Save size={12} /> {savingOpening ? "…" : "Save"}
                  </button>
                  <button
                    onClick={() => setEditingOpening(false)}
                    className="h-8 px-2 text-xs text-gray-500 hover:text-black"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
          {!hideMoney && (
            <>
            <div>
              <div className="small-label text-gray-500">Gas Balance</div>
              <div className={`data-num text-3xl ${data.closing_balance >= 0 ? "text-[#B45309]" : "text-[#15803D]"}`}
                data-testid="ledger-closing-balance">
                ₹ {formatMoney(data.closing_balance)}
              </div>
            </div>
            <div>
              <div className="small-label text-gray-500">Materials Balance</div>
              <div className={`data-num text-3xl ${(data.materials_balance || 0) >= 0 ? "text-[#B45309]" : "text-[#15803D]"}`}
                data-testid="ledger-materials-balance">
                ₹ {formatMoney(data.materials_balance || 0)}
              </div>
            </div>
            <div className="border-l-2 border-gray-300 pl-4 sm:pl-6">
              <div className="small-label text-gray-500">Total Due</div>
              <div className={`data-num text-3xl font-black ${(data.total_balance || 0) >= 0 ? "text-[#B45309]" : "text-[#15803D]"}`}
                data-testid="ledger-total-balance">
                ₹ {formatMoney(data.total_balance ?? data.closing_balance)}
              </div>
            </div>
            </>
          )}
          <button className="btn-primary flex items-center gap-2 h-11" onClick={downloadPdf} data-testid="pdf-export-button">
            <FileDown size={16} /> Download PDF
          </button>
        </div>
      </div>

      {!hideMoney && (data.pending_challans?.length || 0) > 0 && (
        <div className="card-flat" data-testid="pending-challans-panel">
          <div className="p-4 border-b border-gray-200 small-label text-gray-500 flex items-center justify-between">
            <span>Pending Challans · {p.type === "customer" ? "unpaid invoices" : "unpaid bills"}</span>
            <span className="text-xs text-gray-500 normal-case">
              {data.pending_challans.length} pending · Total ₹ <span className="data-num font-bold text-[#B45309]">{formatMoney(data.pending_challans.reduce((s, b) => s + (b.remaining || 0), 0))}</span>
              {data.pending_challans.some((b) => b.bill_mismatch) && (
                <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-[#FEE2E2] text-[#B91C1C] font-bold">
                  {data.pending_challans.filter((b) => b.bill_mismatch).length} bill mismatch
                </span>
              )}
            </span>
          </div>
          <div className="overflow-auto">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Date</th>
                  <th className="text-left">Challan No.</th>
                  <th className="text-left">Bill No.</th>
                  <th className="num">Bill Amt</th>
                  <th className="num">Sale Amt</th>
                  <th className="num">Remaining</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.pending_challans.map((b, i) => {
                  const isPartial = b.remaining > 0.01 && b.remaining < b.amount - 0.01;
                  const isOpening = b.type === "opening";
                  const mismatch = !!b.bill_mismatch;
                  return (
                    <tr key={b.id || i} className={mismatch ? "bg-[#FEE2E2]/60" : ""} data-testid={`pending-challan-row-${i}`}>
                      <td className="data-num text-xs">{b.date || "—"}</td>
                      <td className="data-num text-sm font-semibold">
                        {isOpening ? <span className="text-gray-500 italic">Opening Balance</span> : (b.challan_no || <span className="text-gray-400 italic">no challan #</span>)}
                      </td>
                      <td className="data-num text-sm">
                        {isOpening ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            {b.bill_no
                              ? <span className="font-semibold">{b.bill_no}</span>
                              : <span className="text-gray-400 italic text-xs">not added</span>}
                            {b.id && (
                              <button
                                onClick={() => setEditingBill({
                                  txn_id: b.id, date: b.date, challan_no: b.challan_no,
                                  bill_no: b.bill_no || "", bill_amount: b.bill_amount || 0,
                                  amount: b.amount,
                                })}
                                className="text-gray-400 hover:text-black p-0.5 rounded-sm hover:bg-gray-100"
                                title={b.bill_no ? "Edit bill" : "Add bill"}
                                data-testid={`pending-bill-edit-${i}`}
                              >
                                <Pencil size={11} />
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td className={`num data-num text-xs ${mismatch ? "text-[#B91C1C] font-bold" : "text-gray-600"}`}>
                        {b.bill_amount > 0 ? formatMoney(b.bill_amount) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className={`num data-num text-xs ${mismatch ? "text-[#B91C1C] font-bold" : "text-gray-600"}`}>
                        {formatMoney(b.amount)}
                      </td>
                      <td className="num data-num font-bold text-[#B45309]">{formatMoney(b.remaining)}</td>
                      <td>
                        {mismatch ? (
                          <span className="inline-block px-2 py-0.5 text-xs rounded-sm font-semibold bg-[#B91C1C] text-white" title={`Bill ₹${formatMoney(b.bill_amount)} vs Sale ₹${formatMoney(b.amount)} — diff ₹${formatMoney(Math.abs(b.bill_amount - b.amount))}`}>
                            Mismatch
                          </span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 text-xs rounded-sm font-semibold ${isPartial ? "bg-amber-100 text-[#B45309]" : "bg-red-100 text-[#B91C1C]"}`}>
                            {isPartial ? "Partly Paid" : "Unpaid"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-gray-200 text-xs text-gray-500">
            Auto-matched using FIFO — oldest bills consume payments first.
            {data.pending_challans.some((b) => b.bill_mismatch) && (
              <> <span className="text-[#B91C1C] font-semibold">Red rows</span> flag a mismatch between the bill amount you entered and the computed sale amount.</>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flat">
          <div className="p-4 border-b border-gray-200 small-label text-gray-500 flex items-center justify-between">
            <span>Gas / Amount Ledger</span>
            {isAdmin && <span className="text-xs text-gray-400 normal-case">Click <Pencil size={11} className="inline" /> to edit rates / payment</span>}
          </div>
          {hideMoney ? (
            <div className="p-8 text-center text-gray-500 flex flex-col items-center gap-3" data-testid="ledger-amount-hidden">
              <EyeOff size={28} className="text-gray-400" />
              <div className="text-sm">Amount ledger is hidden for your role.</div>
              <div className="text-xs text-gray-400">Cylinder movement is visible below.</div>
            </div>
          ) : (
          <div className="overflow-auto max-h-[520px]">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Date</th><th className="text-left">Description</th>
                  <th className="num">KG</th>
                  <th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th>
                  {isAdmin && <th className="text-right"></th>}
                </tr>
              </thead>
              <tbody>
                {data.gas_rows.map((r, i) => {
                  const kgVal = (() => {
                    if (r.kind === "bulk_purchase") return Number(r.kg || 0);
                    if (r.kind === "sale" || r.kind === "purchase") {
                      return (r.items || []).reduce((s, it) => {
                        const n = parseFloat(String(it.size || "").toLowerCase().replace("kg", "").trim());
                        const sk = isNaN(n) ? 0 : n;
                        return s + Number(it.filled_qty || 0) * sk;
                      }, 0);
                    }
                    return 0;
                  })();
                  const isMismatch = !!r.bill_mismatch;
                  return (
                  <tr key={i} className={isMismatch ? "bg-[#FEE2E2]/60" : ""} data-testid={`ledger-row-${i}`}>
                    <td className="data-num text-xs">{r.date || "—"}</td>
                    <td className="text-sm">
                      {r.description}
                      {isMismatch && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded-sm bg-[#B91C1C] text-white font-bold" title={`Bill ₹${formatMoney(r.bill_amount)} vs Sale ₹${formatMoney((r.debit||0)+(r.credit||0))}`}>
                          MISMATCH
                        </span>
                      )}
                    </td>
                    <td className="num data-num text-xs text-gray-700">{kgVal > 0 ? formatMoney(Math.round(kgVal * 100) / 100) : ""}</td>
                    <td className={`num ${isMismatch ? "text-[#B91C1C] font-bold" : "text-[#B91C1C]"}`}>{r.debit ? formatMoney(r.debit) : ""}</td>
                    <td className={`num ${isMismatch ? "text-[#B91C1C] font-bold" : "text-[#15803D]"}`}>{r.credit ? formatMoney(r.credit) : ""}</td>
                    <td className="num font-bold">{formatMoney(r.balance)}</td>
                    {isAdmin && (
                      <td className="text-right">
                        {r.txn_id && (r.kind === "sale" || r.kind === "purchase") && (r.items?.length || 0) > 0 ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => setEditingBill({
                                txn_id: r.txn_id, date: r.date, challan_no: r.challan_no,
                                bill_no: r.bill_no || "", bill_amount: r.bill_amount || 0,
                                amount: (r.debit || 0) + (r.credit || 0),
                              })}
                              className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-gray-100 ${isMismatch ? "text-[#B91C1C] hover:text-[#B91C1C]" : "text-gray-500 hover:text-black"}`}
                              title={r.bill_no ? `Bill: ${r.bill_no}${isMismatch ? " (mismatch)" : ""}` : "Add bill number"}
                              data-testid={`edit-bill-${i}`}
                            >
                              <Receipt size={12} />
                            </button>
                            <button
                              onClick={() => setEditingRow(r)}
                              className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-gray-100"
                              title="Edit rates"
                              data-testid={`edit-rate-${i}`}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              onClick={() => deleteTxn(r)}
                              className="text-xs text-gray-400 hover:text-[#B91C1C] inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-red-50"
                              title={`Delete ${r.kind}`}
                              data-testid={`delete-txn-${i}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ) : r.payment_id ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => setEditingPayment(r)}
                              className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-gray-100"
                              title="Edit payment"
                              data-testid={`edit-payment-${i}`}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              onClick={() => deletePayment(r)}
                              className="text-xs text-gray-400 hover:text-[#B91C1C] inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-red-50"
                              title="Delete payment"
                              data-testid={`delete-payment-${i}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <div className="card-flat">
          <div className="p-4 border-b border-gray-200 small-label text-gray-500">Cylinder Balance</div>
          <table className="table-dense w-full">
            <thead>
              <tr><th className="text-left">Brand</th><th className="text-left">Size</th><th className="num">Pending MT</th></tr>
            </thead>
            <tbody>
              {data.cylinder_summary.map((r, i) => (
                <tr key={i}>
                  <td className="font-medium">{r.brand}</td>
                  <td>{r.size}</td>
                  <td className={`num ${r.pending > 0 ? "text-[#B45309] font-bold" : r.pending < 0 ? "text-[#15803D]" : "text-gray-500"}`}>{r.pending}</td>
                </tr>
              ))}
              {data.cylinder_summary.length === 0 && <tr><td colSpan="3" className="py-4 text-center text-gray-400 text-sm">No cylinders yet</td></tr>}
            </tbody>
          </table>
          <div className="p-4 border-t border-gray-200 text-xs text-gray-500">
            Positive = cylinders still with {p.type}. We expect {p.type === "customer" ? "customer to return MT" : "vendor to receive MT back"}.
          </div>
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500">Cylinder Movement Detail</div>
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th><th className="text-left">Challan</th><th className="text-left">Type</th>
              <th className="text-left">Brand</th><th className="text-left">Size</th>
              <th className="num">Filled</th><th className="num">MT</th><th className="num">Pending</th>
            </tr>
          </thead>
          <tbody>
            {data.cylinder_rows.map((r, i) => (
              <tr key={i}>
                <td className="data-num text-xs">{r.date}</td>
                <td className="data-num text-xs">{r.challan_no || "—"}</td>
                <td className="capitalize">{r.kind}</td>
                <td>{r.brand}</td>
                <td>{r.size}</td>
                <td className="num text-[#15803D]">{r.filled_qty}</td>
                <td className="num text-[#B91C1C]">{r.mt_qty}</td>
                <td className="num font-bold">{r.pending}</td>
              </tr>
            ))}
            {data.cylinder_rows.length === 0 && <tr><td colSpan="8" className="py-4 text-center text-gray-400 text-sm">No movements yet</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card-flat" data-testid="materials-ledger-section">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500 flex items-center justify-between">
          <span>Materials Ledger · Industrial Supplies</span>
          <span className="normal-case text-xs">
            Balance: <span className={`data-num font-bold ${(data.materials_balance || 0) > 0 ? "text-[#B45309]" : "text-[#15803D]"}`}>₹ {formatMoney(data.materials_balance || 0)}</span>
          </span>
        </div>
        {hideMoney ? (
          <div className="p-6 text-center text-gray-400 text-sm">Materials amounts hidden for your role.</div>
        ) : (
          <div className="overflow-auto max-h-[420px]">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Date</th><th className="text-left">Description</th>
                  <th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(data.materials_rows || []).map((r, i) => (
                  <tr key={i} data-testid={`materials-row-${i}`}>
                    <td className="data-num text-xs">{r.date || "—"}</td>
                    <td className="text-sm">
                      {r.description}
                      {(r.ind_items || []).length > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {r.ind_items.map((it, j) => (
                            <span key={j} className="inline-block mr-3">
                              {it.name}{it.brand ? ` ${it.brand}` : ""} × <span className="data-num">{it.qty}</span> {it.unit} @ ₹{formatMoney(it.rate)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="num text-[#B91C1C]">{r.debit ? formatMoney(r.debit) : ""}</td>
                    <td className="num text-[#15803D]">{r.credit ? formatMoney(r.credit) : ""}</td>
                    <td className="num font-bold">{formatMoney(r.balance)}</td>
                  </tr>
                ))}
                {(data.materials_rows || []).length === 0 && (
                  <tr><td colSpan="5" className="py-6 text-center text-gray-400 text-sm">No materials transactions yet. Use Industrial Purchase / Sale from the sidebar.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingRow && (        <RateEditModal
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={() => { setEditingRow(null); load(); }}
        />
      )}

      {editingPayment && (
        <PaymentEditModal
          row={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSaved={() => { setEditingPayment(null); load(); }}
        />
      )}

      {editingBill && (
        <BillNoEditModal
          row={editingBill}
          onClose={() => setEditingBill(null)}
          onSaved={() => { setEditingBill(null); load(); }}
        />
      )}
    </div>
  );
}
